import React, { useState, useEffect } from 'react'
import { useParams, useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { ArrowLeft, Calendar, MapPin, Clock, Save, Shirt, FileText } from 'lucide-react'

const eventTypes = [
  { value: 'practice', label: 'Practice' },
  { value: 'game', label: 'Game' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'social', label: 'Social Event' },
]

export default function PortalEventForm() {
  const { eventId, teamId } = useParams()
  const { team, canManage } = useOutletContext()
  const navigate = useNavigate()
  const isEdit = Boolean(eventId)

  const [form, setForm] = useState({
    title: '',
    event_type: 'practice',
    event_date: '',
    end_date: '',
    location: '',
    uniform_color: '',
    arrival_time: '',
    description: '',
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(isEdit)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEdit) {
      fetchEvent()
    }
  }, [eventId])

  const fetchEvent = async () => {
    try {
      const data = await portalRequest(`/api/events/${eventId}`)
      setForm({
        title: data.title || '',
        event_type: data.event_type || 'practice',
        event_date: data.event_date ? data.event_date.slice(0, 16) : '',
        end_date: data.end_date ? data.end_date.slice(0, 16) : '',
        location: data.location || '',
        uniform_color: data.uniform_color || '',
        arrival_time: data.arrival_time || '',
        description: data.description || '',
        notes: data.notes || '',
      })
    } catch (err) {
      setError('Failed to load event')
    } finally {
      setFetchLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const payload = {
      ...form,
      team_id: team.team_id,
      event_date: form.event_date ? new Date(form.event_date).toISOString() : undefined,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : undefined,
    }
    // Remove empty optional fields
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') delete payload[k]
    })

    try {
      if (isEdit) {
        await portalRequest(`/api/events/${eventId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        })
        navigate(`/portal/team/${team.team_id}/events/${eventId}`)
      } else {
        const data = await portalRequest('/api/events', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
        navigate(`/portal/team/${team.team_id}/schedule`)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="text-center py-20 text-zinc-400">
        You don't have permission to manage events.
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate(`/portal/team/${team.team_id}/schedule`)}
        className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Schedule</span>
      </button>

      <div>
        <h1 className="text-2xl font-bold text-white">
          {isEdit ? 'Edit Event' : 'Create Event'}
        </h1>
        <p className="text-zinc-400 mt-1">
          {isEdit ? 'Update event details' : 'Add a new event to the team schedule'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-400 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Event Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
              placeholder="e.g., Practice at Central Park"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Event Type</label>
            <select
              value={form.event_type}
              onChange={(e) => update('event_type', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
            >
              {eventTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={form.event_date}
                onChange={(e) => update('event_date', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1" />
                End Date & Time
              </label>
              <input
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => update('end_date', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
              placeholder="e.g., Central Park Field 3"
            />
          </div>

          {/* Arrival & Uniform */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1" />
                Arrival Time
              </label>
              <input
                type="text"
                value={form.arrival_time}
                onChange={(e) => update('arrival_time', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                placeholder="e.g., 30 min before"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                <Shirt className="w-4 h-4 inline mr-1" />
                Uniform/Color
              </label>
              <input
                type="text"
                value={form.uniform_color}
                onChange={(e) => update('uniform_color', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                placeholder="e.g., White jerseys"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all resize-none"
              placeholder="Additional details about the event..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Notes (private)</label>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={2}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all resize-none"
              placeholder="Internal notes..."
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 transition-all"
          >
            {loading ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isEdit ? 'Save Changes' : 'Create Event'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/portal/team/${team.team_id}/schedule`)}
            className="px-6 py-3 border border-zinc-700 text-zinc-300 rounded-xl font-medium hover:bg-zinc-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
